import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { X, Plus, ImageIcon, Loader2 } from 'lucide-react'
import { uploadImageToStorageSecure } from '@/lib/secureFileUpload'
import type { ArtistOnboarding } from '@/types/models'

interface ArtistOnboardingStep2Props {
  onboarding: ArtistOnboarding
  onDataChange: (data: Partial<ArtistOnboarding>) => void
}

export function ArtistOnboardingStep2({ onboarding, onDataChange }: ArtistOnboardingStep2Props) {
  const [tattooImageUrls, setTattooImageUrls] = useState<string[]>(
    onboarding.tattooImageUrls || []
  )
  const [imageLoadErrors, setImageLoadErrors] = useState<Set<string>>(new Set())
  const [newImagePreviews, setNewImagePreviews] = useState<string[]>([])
  const [uploadingImages, setUploadingImages] = useState<Set<string>>(new Set())

  // Sync local state with onboarding prop when it changes
  useEffect(() => {
    if (onboarding.tattooImageUrls) {
      setTattooImageUrls(onboarding.tattooImageUrls)
    }
  }, [onboarding.tattooImageUrls])

  // Update parent component when tattoo images change
  useEffect(() => {
    onDataChange({ tattooImageUrls })
  }, [tattooImageUrls]) // Remove onDataChange from dependencies to prevent infinite loop

  const handleImageError = (url: string) => {
    setImageLoadErrors(prev => new Set(prev).add(url))
  }

  const removeImage = (urlToRemove: string) => {
    setTattooImageUrls(prev => prev.filter(url => url !== urlToRemove))
  }

  const handleNewImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [])
    
    files.forEach(async (file) => {
      const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      
      // Create preview with FileReader for immediate display
      const reader = new FileReader()
      reader.onload = (e) => {
        const result = e.target?.result as string
        setNewImagePreviews(prev => [...prev, result])
      }
      reader.readAsDataURL(file)
      
      // Start uploading to Firebase Storage
      setUploadingImages(prev => new Set([...prev, tempId]))
      
      try {
        // Upload to Firebase Storage using onboardings path (allows uploads during onboarding)
        // Use onboarding.id or userId as identifier
        const onboardingIdentifier = onboarding.id || onboarding.userId || onboarding.displayName
        const firebaseUrl = await uploadImageToStorageSecure(
          file,
          `onboardings/${onboardingIdentifier}/portfolio`,
          onboardingIdentifier
        )
        
        // Add the Firebase URL to tattoo images
        setTattooImageUrls(prev => [...prev, firebaseUrl])
      } catch (error) {
        console.error('Failed to upload portfolio image:', error)
        // TODO: Show error toast
        // Remove the preview since upload failed
        const reader = new FileReader()
        reader.onload = (e) => {
          const result = e.target?.result as string
          setNewImagePreviews(prev => prev.filter(preview => preview !== result))
        }
        reader.readAsDataURL(file)
      } finally {
        setUploadingImages(prev => {
          const next = new Set(prev)
          next.delete(tempId)
          return next
        })
      }
    })
  }

  const removeNewImage = (index: number) => {
    const previewToRemove = newImagePreviews[index]
    setNewImagePreviews(prev => prev.filter((_, i) => i !== index))
    setTattooImageUrls(prev => prev.filter(url => url !== previewToRemove))
  }

  const validOriginalImages = onboarding.postUrls?.filter(url => !imageLoadErrors.has(url)) || []
  const selectedOriginalImages = tattooImageUrls.filter(url => 
    onboarding.postUrls?.includes(url) && !imageLoadErrors.has(url)
  )
  
  // Get uploaded images that are not from Instagram (previously uploaded images from database)
  const uploadedImages = tattooImageUrls.filter(url => 
    !onboarding.postUrls?.includes(url) && 
    !newImagePreviews.includes(url) &&
    !imageLoadErrors.has(url)
  )

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-semibold">
              3
            </div>
            Portfolio Images
          </CardTitle>
          <CardDescription>
            Review your tattoo portfolio and customize it for potential clients. Remove any images you don't want displayed and add more if needed.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Statistics */}
          <div className="flex items-center gap-4 text-sm">
            <Badge variant={tattooImageUrls.length >= 10 ? "default" : "destructive"}>
              {tattooImageUrls.length} images selected
            </Badge>
            <Badge variant="secondary">
              {selectedOriginalImages.length} from instagram
            </Badge>
            <Badge variant="secondary">
              {uploadedImages.length} uploaded
            </Badge>
            {newImagePreviews.length > 0 && (
              <Badge variant="secondary">
                {newImagePreviews.length} uploading now
              </Badge>
            )}
            {tattooImageUrls.length < 10 && (
              <Badge variant="destructive">
                Need {10 - tattooImageUrls.length} more (min 10)
              </Badge>
            )}
          </div>

          {/* Original Instagram Images */}
          {validOriginalImages.length > 0 && (
            <div className="space-y-3">
              <h3 className="font-semibold">From Instagram</h3>
              <p className="text-sm text-muted-foreground">
                These tattoo images were carefully selected from your Instagram by our team. Remove any you don't want to showcase to potential clients.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {validOriginalImages.map((url, index) => {
                  const isSelected = tattooImageUrls.includes(url)
                  
                  return (
                    <div
                      key={index}
                      className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                        isSelected 
                          ? 'border-primary shadow-lg' 
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <img
                        src={url}
                        alt={`Instagram post ${index + 1}`}
                        className="w-full h-full object-cover cursor-pointer"
                        onError={() => handleImageError(url)}
                        onClick={() => {
                          if (isSelected) {
                            removeImage(url)
                          } else {
                            setTattooImageUrls(prev => [...prev, url])
                          }
                        }}
                        loading="lazy"
                      />
                      
                      {isSelected && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            removeImage(url)
                          }}
                          className="absolute top-2 right-2 bg-destructive text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-destructive/90 transition-colors"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      )}
                      
                      {!isSelected && (
                        <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                          <Plus className="w-6 h-6 text-white" />
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Upload New Images */}
          <div className="space-y-3">
            <h3 className="font-semibold">Upload Additional Tattoo Images</h3>
            <p className="text-sm text-muted-foreground">
              Add more of your best tattoo work to reach the recommended 20 images.
            </p>
            
            <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleNewImageUpload}
                className="hidden"
                id="new-images-upload"
              />
              <label
                htmlFor="new-images-upload"
                className="cursor-pointer flex flex-col items-center gap-3"
              >
                <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center">
                  <ImageIcon className="w-6 h-6 text-muted-foreground" />
                </div>
                <div>
                  <p className="font-medium">Upload Images</p>
                  <p className="text-sm text-muted-foreground">
                    Click to select multiple images
                  </p>
                </div>
              </label>
            </div>

            {/* Previously Uploaded Images (from database) */}
            {uploadedImages.length > 0 && (
              <div className="space-y-3">
                <h4 className="font-medium">Your Uploaded Images</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {uploadedImages.map((url, index) => (
                    <div
                      key={index}
                      className="relative aspect-square rounded-lg overflow-hidden border-2 border-primary shadow-lg"
                    >
                      <img
                        src={url}
                        alt={`Uploaded image ${index + 1}`}
                        className="w-full h-full object-cover"
                        onError={() => handleImageError(url)}
                        loading="lazy"
                      />
                      <button
                        onClick={() => removeImage(url)}
                        className="absolute top-2 right-2 bg-destructive text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-destructive/90 transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                      <div className="absolute bottom-2 left-2">
                        <Badge variant="secondary" className="text-xs">
                          Uploaded
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Newly Uploaded Images (temporary previews during current session) */}
            {newImagePreviews.length > 0 && (
              <div className="space-y-3">
                <h4 className="font-medium">Uploading Now</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {newImagePreviews.map((preview, index) => (
                    <div
                      key={index}
                      className="relative aspect-square rounded-lg overflow-hidden border-2 border-primary shadow-lg"
                    >
                      <img
                        src={preview}
                        alt={`New upload ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                      <button
                        onClick={() => removeNewImage(index)}
                        className="absolute top-2 right-2 bg-destructive text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-destructive/90 transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                      <div className="absolute bottom-2 left-2">
                        <Badge variant="secondary" className="text-xs">
                          New
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Upload Progress Indicator */}
            {uploadingImages.size > 0 && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Uploading {uploadingImages.size} image{uploadingImages.size > 1 ? 's' : ''}...</span>
              </div>
            )}
          </div>


          {/* Guidelines */}
          <Card className={`${tattooImageUrls.length < 10 ? 'bg-red-50 border-red-200' : tattooImageUrls.length >= 20 ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'}`}>
            <CardContent className="pt-6">
              <h4 className="font-semibold mb-2">Portfolio Requirements</h4>
              <ul className="text-sm space-y-1">
                <li className={`flex items-center gap-2 ${tattooImageUrls.length >= 10 ? 'text-green-700' : 'text-red-700'}`}>
                  <span className="w-1 h-1 bg-current rounded-full"></span>
                  <strong>Minimum 10 images</strong> - Currently have {tattooImageUrls.length}
                </li>
                <li className={`flex items-center gap-2 ${tattooImageUrls.length >= 20 ? 'text-green-700' : 'text-blue-700'}`}>
                  <span className="w-1 h-1 bg-current rounded-full"></span>
                  <strong>Goal: 20 images</strong> for best client attraction
                </li>
                <li className="flex items-center gap-2 text-muted-foreground">
                  <span className="w-1 h-1 bg-current rounded-full"></span>
                  Images should be clear and well-lit
                </li>
                <li className="flex items-center gap-2 text-muted-foreground">
                  <span className="w-1 h-1 bg-current rounded-full"></span>
                  Include a variety of styles and sizes
                </li>
              </ul>
            </CardContent>
          </Card>
        </CardContent>
      </Card>
    </div>
  )
}
